import React, { useEffect, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

const permissionOptions = [
    { key: "addEditUsers", label: "Add and edit users" },
    { key: "uploadEditOwnVideo", label: "Upload and edit files" },
    { key: "comment", label: "Comment on files" },
    { key: "openMessage", label: "Open messages" },
    { key: "personalMessage", label: "Send personal messages" },
    { key: "viewCalendar", label: "View team or league calendar" },
    { key: "editCalendar", label: "Edit calendar" },
    { key: "exportCalendar", label: "Export calendar" },
    { key: "createOwnPlaylist", label: "Create own playlists" },
    { key: "analysis", label: "Use analysis tools" },
    { key: "createSurveys", label: "Create polls and surveys" },
    { key: "shareFiles", label: "Share files" },
    { key: "createProjects", label: "Create projects" },
];

export default function UserPermissionsDialog(props) {
    const { onClose, open } = props;

    const [permissions, setPermissions] = useState({
        addEditUsers: false,
        uploadEditOwnVideo: true,
        comment: true,
        openMessage: true,
        personalMessage: false,
        viewCalendar: true,
        editCalendar: false,
        exportCalendar: true,
        createOwnPlaylist: true,
        analysis: true,
        createSurveys: false,
        shareFiles: false,
        createProjects: true,
    });

    useEffect(() => {
        if (open && props.permissions) {
            setPermissions({ ...props.permissions });
        }
    }, [open, props.permissions]);

    const handleDone = () => {
        onClose(permissions);
    };

    const handleCancel = () => {
        onClose();
    };

    const handleChange = (event) => {
        setPermissions({
            ...permissions,
            [event.target.name]: event.target.checked,
        });
    };

    return (
        <Dialog
            onClose={handleCancel}
            aria-labelledby="user-permissions-dialog-title"
            open={open}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <SecurityOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="user-permissions-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        User permissions
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose the features available to this user. Changes are
                        not saved until you save the user.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close user permissions"
                    onClick={handleCancel}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                        },
                        gap: 1.25,
                    }}
                >
                    {permissionOptions.map((permission) => (
                        <Box
                            key={permission.key}
                            sx={{
                                minHeight: 62,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                border: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    width: "100%",
                                    m: 0,
                                    justifyContent: "space-between",
                                    gap: 1,
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: 14,
                                        fontWeight: 700,
                                    },
                                }}
                                labelPlacement="start"
                                control={
                                    <Switch
                                        checked={Boolean(
                                            permissions[permission.key],
                                        )}
                                        onChange={handleChange}
                                        name={permission.key}
                                        color="primary"
                                    />
                                }
                                label={permission.label}
                            />
                        </Box>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button onClick={handleCancel} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleDone}
                    variant="contained"
                    disableElevation
                    startIcon={<CheckIcon />}
                >
                    Apply to draft
                </Button>
            </DialogActions>
        </Dialog>
    );
}
